import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  FunctionField,
  EditButton,
  ReferenceField,
  TextInput,
  TextField,
  DateField,
  useEditContext,
  usePermissions,
  useTranslate,
  FormDataConsumer,
  ReferenceInput,
  ReferenceArrayInput,
  AutocompleteInput,
  AutocompleteArrayInput,
  BooleanInput,
  required,
} from 'react-admin';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Link,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import EmptyList from '../components/misc/EmptyList';
import SelectUserByEmailInput from '../components/form/SelectUserByEmailInput';
import ResourceListTabs from '../components/misc/ResourceListTabs';
import { ApplicationDatagrid } from './eventCreationApplications';
import { usePendingApplicationCount } from '../hooks/usePendingApplications';
import {
  SALE_AGENT_APPLICATION_FILTERS,
  TABBED_LIST_SX,
  TabbedListActionsPlaceholder,
} from '../components/misc/tabbedListLayout';
import ListQSearchBar from '../components/misc/ListQSearchBar';

const entity = 'saleAgents';

const getApiBase = () =>
  (process.env.REACT_APP_API_URL ?? 'http://localhost:3001').replace(/\/+$/, '');

const PartnerFeedBanner = ({ record }) => {
  const translate = useTranslate();
  const websiteUrl = String(record?.websiteUrl || '').trim();
  if (!websiteUrl || !record?.id) return null;

  const feedUrl = `${getApiBase()}/partners/feed/${encodeURIComponent(record.id)}`.replace(
    /([^:]\/)\/+/g,
    '$1',
  );

  const copy = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(feedUrl);
    }
  };

  return (
    <Alert
      severity="info"
      sx={{ width: '100%', mb: 2, alignItems: 'center' }}
      action={
        <Tooltip title={translate('saleAgents.copy_feed', { _: 'Kopyala' })}>
          <IconButton color="inherit" size="small" onClick={copy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {translate('saleAgents.feed_title', {
          _: 'Site entegrasyon endpoint’i',
        })}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {translate('saleAgents.feed_help', {
          _: 'Bu URL’den partner etkinliklerini JSON olarak çekebilirsiniz.',
        })}
      </Typography>
      <Link
        href={feedUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ wordBreak: 'break-all' }}
      >
        {feedUrl}
      </Link>
    </Alert>
  );
};

const PartnerAccordion = ({ title, children, defaultExpanded = false }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    sx={{
      width: '100%',
      mb: 1,
      border: '1px solid #e0e0e0',
      borderRadius: '8px !important',
      '&:before': { display: 'none' },
      boxShadow: 'none',
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography fontWeight={600}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
  </Accordion>
);

const PartnerScopeFields = ({ record }) => {
  const translate = useTranslate();

  return (
    <React.Fragment>
      <h3 style={{ marginTop: 24 }}>
        {translate('saleAgents.partner_section', { _: 'Partner Portal Atamaları' })}
      </h3>

      <FormDataConsumer>
        {({ formData }) =>
          record?.id && String(formData?.websiteUrl || '').trim() ? (
            <PartnerFeedBanner record={{ ...record, websiteUrl: formData.websiteUrl }} />
          ) : null
        }
      </FormDataConsumer>

      <PartnerAccordion
        title={translate('saleAgents.key_website', {
          _: 'Web Site / Uygulama Linki',
        })}
        defaultExpanded
      >
        <TextInput
          source="websiteUrl"
          label={translate('saleAgents.website_url', {
            _: 'Web site veya uygulama linki',
          })}
          fullWidth
          helperText={translate('saleAgents.website_url_help', {
            _: 'Doldurulursa partner feed endpoint’i açılır (sitelerine entegre edebilirler).',
          })}
        />
      </PartnerAccordion>

      <PartnerAccordion
        title={translate('saleAgents.key_affiliate', { _: 'Affiliate Kodu' })}
        defaultExpanded
      >
        <ReferenceInput
          source="affiliateCode"
          reference="affiliates"
          label={translate('saleAgents.affiliate_code', { _: 'Affiliate Kodu' })}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={100}
        >
          <AutocompleteInput
            optionText={(r) => (r ? `${r.name || ''} (${r.code || r.id})`.trim() : '')}
            matchSuggestion={() => true}
            filterToQuery={(searchText) => ({ q: searchText })}
            fullWidth
            clearOnBlur={false}
          />
        </ReferenceInput>
      </PartnerAccordion>

      <PartnerAccordion title={translate('saleAgents.key_events', { _: 'Etkinlikler' })}>
        <ReferenceArrayInput
          source="events"
          reference="events-v2"
          label={translate('saleAgents.events', { _: 'Etkinlikler' })}
          sort={{ field: 'startdate', order: 'ASC' }}
          perPage={50}
          filter={{ status: 'notPast', isActive: 'true' }}
        >
          <AutocompleteArrayInput
            optionText="name"
            fullWidth
            matchSuggestion={() => true}
            filterToQuery={(searchText) => ({
              q: searchText,
              status: 'notPast',
              isActive: 'true',
            })}
            clearOnBlur={false}
          />
        </ReferenceArrayInput>
      </PartnerAccordion>

      <PartnerAccordion title={translate('saleAgents.key_venues', { _: 'Mekanlar' })}>
        <ReferenceArrayInput
          source="venues"
          reference="venues"
          label={translate('saleAgents.venues', { _: 'Mekanlar' })}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={100}
        >
          <AutocompleteArrayInput
            optionText="name"
            fullWidth
            matchSuggestion={() => true}
            filterToQuery={(searchText) => ({ name: searchText })}
            clearOnBlur={false}
          />
        </ReferenceArrayInput>
      </PartnerAccordion>

      <PartnerAccordion
        title={translate('saleAgents.key_orgs', {
          _: 'Organizasyon Şirketleri',
        })}
      >
        <ReferenceArrayInput
          source="organisationCompanies"
          reference="organisationCompanies"
          label={translate('saleAgents.orgs', {
            _: 'Organizasyon Şirketleri',
          })}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={100}
        >
          <AutocompleteArrayInput
            optionText="name"
            fullWidth
            matchSuggestion={() => true}
            filterToQuery={(searchText) => ({ name: searchText })}
            clearOnBlur={false}
          />
        </ReferenceArrayInput>
      </PartnerAccordion>

      <PartnerAccordion
        title={translate('saleAgents.key_address', {
          _: 'Fiziksel Adres',
        })}
      >
        <BooleanInput
          source="hasPhysicalAddress"
          label={translate('saleAgents.has_physical_address', {
            _: 'Fiziksel adresi var mı?',
          })}
          defaultValue={false}
        />
        <FormDataConsumer>
          {({ formData }) =>
            formData?.hasPhysicalAddress ? (
              <TextInput
                source="physicalAddress"
                label={translate('saleAgents.physical_address', {
                  _: 'Konum / Açık Adres',
                })}
                fullWidth
                multiline
                rows={3}
                helperText={translate('saleAgents.physical_address_help', {
                  _: 'Adres veya konum bilgisini girin',
                })}
              />
            ) : null
          }
        </FormDataConsumer>
      </PartnerAccordion>
    </React.Fragment>
  );
};

export const SaleAgentList = (props) => {
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const [searchParams] = useSearchParams();
  const isApplications = searchParams.get('tab') === 'applications';
  const pendingApplications = usePendingApplicationCount('salesAgent');

  const canViewApplications = permissions?.crole === 'admin' || permissions?.crole === 'operator';

  const saleAgentTabs = [
    { id: 'list', label: translate('saleAgents.tab_list', { _: 'Satış Temsilcileri' }) },
    {
      id: 'applications',
      label: translate('saleAgents.tab_applications', { _: 'Başvurular' }),
      badge: pendingApplications,
    },
  ];
  const showingApplications = isApplications && canViewApplications;

  return (
    <List
      {...props}
      resource={showingApplications ? 'eventCreationApplications' : entity}
      filter={showingApplications ? { companyType: 'salesAgent', isChecked: false } : undefined}
      empty={<EmptyList entity={showingApplications ? 'eventCreationApplications' : entity} />}
      title={
        showingApplications
          ? translate('saleAgents.title_applications', { _: 'Satış Temsilcisi Başvuruları' })
          : translate('saleAgents.title', { _: 'Satış Temsilcileri' })
      }
      filters={showingApplications ? SALE_AGENT_APPLICATION_FILTERS : undefined}
      actions={showingApplications ? <TabbedListActionsPlaceholder /> : undefined}
      exporter={false}
      sort={showingApplications ? { field: 'createdate', order: 'DESC' } : undefined}
      perPage={showingApplications ? 25 : 100}
      sx={TABBED_LIST_SX}
    >
      {canViewApplications ? (
        <ResourceListTabs
          activeTab={showingApplications ? 'applications' : 'list'}
          tabs={saleAgentTabs}
        />
      ) : null}
      {!showingApplications ? (
        <ListQSearchBar placeholderKey="saleAgents.search_placeholder" />
      ) : null}
      {showingApplications ? (
        <ApplicationDatagrid showSlug={false} />
      ) : (
        <Datagrid>
          <TextField source="name" label={translate('saleAgents.name', { _: 'İsim' })} />
          <FunctionField
            label={translate('saleAgents.users', { _: 'Kullanıcılar' })}
            render={(r) => {
              const ids = Array.isArray(r?.users)
                ? r.users.filter(Boolean)
                : r?.user
                  ? [r.user]
                  : [];
              if (!ids.length) return '—';
              return translate('saleAgents.users_count', {
                _: '{{count}} kullanıcı',
                count: ids.length,
              });
            }}
          />
          <ReferenceField
            source="user"
            reference="users"
            label={translate('saleAgents.primary_user', { _: 'Ana kullanıcı' })}
            link={false}
            emptyText="—"
          >
            <FunctionField
              render={(u) => `${u?.name || ''} ${u?.surname || ''}`.trim() || u?.email || '—'}
            />
          </ReferenceField>
          <TextField
            source="affiliateCode"
            label={translate('saleAgents.affiliate_code', { _: 'Affiliate Kodu' })}
            emptyText="—"
          />
          <TextField source="notes" label={translate('saleAgents.notes', { _: 'Notlar' })} />
          <DateField
            source="createdate"
            label={translate('saleAgents.created', { _: 'Oluşturulma Tarihi' })}
            showTime
          />
          <EditButton />
        </Datagrid>
      )}
    </List>
  );
};

export const SaleAgentCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create
      title={translate('saleAgents.new_agent', { _: 'Yeni Satış Temsilcisi' })}
      {...props}
      redirect="list"
    >
      <SimpleForm redirect="list">
        <h3>{translate('saleAgents.details_section', { _: 'Detaylar' })}</h3>
        <TextInput
          source="name"
          label={translate('saleAgents.name', { _: 'İsim' })}
          fullWidth
          validate={required()}
        />
        <TextInput
          source="notes"
          label={translate('saleAgents.notes', { _: 'Notlar' })}
          fullWidth
          multiline
          rows={3}
        />

        <h3>{translate('saleAgents.user_section', { _: 'Kullanıcılar' })}</h3>
        <SelectUserByEmailInput source="users" multiple required />

        <PartnerScopeFields />
      </SimpleForm>
    </Create>
  );
};

export const SaleAgentEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit
      title={translate('saleAgents.edit_agent', { _: 'Satış Temsilcisini Düzenle' })}
      {...props}
    >
      <SimpleForm redirect={`/${entity}`}>
        <EditForm />
      </SimpleForm>
    </Edit>
  );
};

const EditForm = () => {
  const translate = useTranslate();
  const { record, isLoading } = useEditContext();

  // Eski kayitlarda sadece user varsa users dizisine cevir
  const usersRecord = React.useMemo(() => {
    if (!record) return record;
    const users = Array.isArray(record.users)
      ? record.users.filter(Boolean)
      : record.user
        ? [record.user]
        : [];
    return { ...record, users };
  }, [record]);

  if (isLoading) return null;

  return (
    <React.Fragment>
      <h3>{translate('saleAgents.details_section', { _: 'Detaylar' })}</h3>
      <TextInput
        source="name"
        label={translate('saleAgents.name', { _: 'İsim' })}
        fullWidth
        validate={required()}
      />
      <TextInput
        source="notes"
        label={translate('saleAgents.notes', { _: 'Notlar' })}
        fullWidth
        multiline
        rows={3}
      />

      <h3>{translate('saleAgents.user_section', { _: 'Kullanıcılar' })}</h3>
      <SelectUserByEmailInput source="users" multiple record={usersRecord} required />

      <PartnerScopeFields record={record} />
    </React.Fragment>
  );
};
